// TODO @cleanup this file needs a new pass through it
import {DEFAULT_PANEL_CONTAINERS, GATE_TYPE, PANEL_TYPE, PANEL_INSERTED_ATTR, FLOW_TYPE, type GateType, type PanelType} from "../Constants";
import {CoverGate} from "../ui/gates/CoverGate";
import {PreviewGate} from "../ui/gates/PreviewGate";
import {evaluatePanelOptions} from "../PanelOptionsLogic";
import {type PanelElementOptions} from "../ui/elements/PanelElement";
import {walletModule} from "./WalletModule";
import {BannerGate} from "../ui/gates/BannerGate";
import {isDeepEqual, isString} from "../../stem-core/base/Utils";
import {PanelElement} from "../ui/elements/PanelElement";
import {merchantFunctionModule} from "./merchant-state/MerchantFunctionModule";
import {CleanupJobs} from "../../stem-core/base/Dispatcher";
import {isMerchantActive} from "./PublicData";
import {GateElement, type InteractiveHandlers} from "../ui/elements/GateElement.js";
import {PopupGate} from "../ui/gates/PopupGate.js";
import {type NodeElementOptions} from "../ui/elements/NodeElement";
import {SessionActionThrottler} from "./SessionActionThrottler";
import {StorageScope} from "./merchant-state/MerchantStorage";

export interface PanelContainerRule extends NodeElementOptions {
    selector: string;
    type?: PanelType;
    gateType?: GateType;
}


// TODO @Mihai merge these, just pass in some options
const gateMap = {
    [GATE_TYPE.cover]: CoverGate,
    [GATE_TYPE.preview]: PreviewGate,
    [GATE_TYPE.banner]: BannerGate,
    [GATE_TYPE.popupGate]: PopupGate
};

// Where a panel goes when it's given no container.
const DEFAULT_CONTAINER_SELECTOR = "body";

// A lazy placement fills a container once it gets within about a screen of the viewport.
const LAZY_ROOT_MARGIN = "100% 0px";

interface PanelPlacementOptions {
    // Fill every container matching the selector, current and future, not just the first one.
    every?: boolean;
    // Leave a container empty until it gets near the viewport.
    lazy?: boolean;
    // Asked before each gate after the first, which for a single placement means one going back up after the router
    // discarded the last. Returning false holds it back.
    beforeRefill?: (() => boolean) | null;
}

// A panel kept up under a selector: in its first match, or with `every`, in each match, including ones rendered
// later. Whoever adds it owns it, and remove() takes down everything it put up.
export class PanelPlacement {
    selector: string;
    panelOptions: PanelElementOptions;
    every: boolean;
    beforeRefill: (() => boolean) | null;
    // Added through addPermanentPlacement, so nobody will remove it.
    permanent = false;
    // Whether any gate has gone up yet.
    filled = false;
    // What this put up, by container. Null where the panel couldn't be created, so that container isn't retried on
    // every scan. A gate the buyer dismissed stays here, so it doesn't come back.
    gates = new Map<Element, InteractiveHandlers | null>();
    // Containers another journey's gate took over from ours. Filling them again would wipe that one.
    taken = new WeakSet<Element>();
    // Set when lazy. A container stays observed until it's filled, so one scrolled past is left alone.
    intersectionObserver: IntersectionObserver | null = null;
    nearViewport = new WeakSet<Element>();

    constructor(selector: string, panelOptions: PanelElementOptions, {every = false, lazy = false, beforeRefill = null}: PanelPlacementOptions = {}) {
        this.selector = selector || DEFAULT_CONTAINER_SELECTOR;
        this.panelOptions = panelOptions;
        this.every = every;
        this.beforeRefill = beforeRefill;
        if (lazy) {
            this.intersectionObserver = new IntersectionObserver((entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        this.nearViewport.add(entry.target);
                    } else {
                        this.nearViewport.delete(entry.target);
                    }
                }
                this.scan();
            }, {rootMargin: LAZY_ROOT_MARGIN});
        }
    }

    scan(): void {
        // Panels can't be created until the SDK has loaded, and resolvePendingRequests() scans once it has. A removed
        // placement can still be asked, by its observer or by a scan of all of them already under way.
        if (panelModule.pendingRequests != null || !panelModule.placements.has(this)) {
            return;
        }
        try {
            this.fillContainers();
        } catch (e) {
            // Reported without holding up the other placements. fill() already recorded the container, so a panel
            // that throws isn't retried every scan.
            merchantFunctionModule.handleException(e, this.panelOptions.journeyAlias);
        }
    }

    fillContainers(): void {
        for (const [container, handler] of this.gates) {
            if (handler?.isReplaced()) {
                this.gates.delete(container);
                this.taken.add(container);
                continue;
            }
            // A gate the router discarded gets replaced on the new page, or in its container if that survived. Asking
            // also tears down a live gate whose container left the page, so the second check only drops entries with
            // nothing up. A single placement keeps a dismissed gate's entry, which is what keeps it from refilling,
            // but every other match is a container of its own.
            if (handler?.isDiscarded() || (this.every && !container.isConnected)) {
                this.gates.delete(container);
                if (this.every) {
                    container.removeAttribute(PANEL_INSERTED_ATTR);
                }
            }
        }
        if (this.every) {
            for (const container of Array.from(document.querySelectorAll(this.selector))) {
                // Our own map covers a stamp the merchant's page stripped from under a live gate.
                if (this.gates.has(container) || this.taken.has(container) || container.getAttribute(PANEL_INSERTED_ATTR) === "true") {
                    continue;
                }
                // Filling an outer match with replaceChildren takes the inner ones off the page
                if (!container.isConnected) {
                    continue;
                }
                if (!this.fill(container)) {
                    return;
                }
            }
        } else if (this.gates.size === 0) {
            const container = document.querySelector(this.selector);
            if (container && !this.taken.has(container)) {
                this.fill(container);
            }
        }
    }

    // Returns false when no more gates can go up for now, which ends the scan.
    fill(container: Element): boolean {
        if (this.intersectionObserver && !this.nearViewport.has(container)) {
            this.intersectionObserver.observe(container);
            return true;
        }
        // The first gate goes up right away, a paywall included. Only what can pile up waits: more containers,
        // and refills.
        if (this.filled && (!panelModule.hasIFrameRoom() || (this.beforeRefill && !this.beforeRefill()))) {
            return false;
        }
        this.filled = true;
        // A refill waits for the container to come near the viewport again
        this.intersectionObserver?.unobserve(container);
        this.nearViewport.delete(container);
        if (this.every) {
            // Keeps the other placements out while this one holds it, a dismissed gate included
            container.setAttribute(PANEL_INSERTED_ATTR, "true");
        }
        // Recorded first, so a panel that throws counts as one that couldn't be created, rather than one to retry
        this.gates.set(container, null);
        this.gates.set(container, panelModule.createPanel(container, {...this.panelOptions}) ?? null);
        return true;
    }

    // Whether the two would put up the same panels in the same places.
    isSameAs(other: PanelPlacement): boolean {
        const describe = (p: PanelPlacement) => [p.selector, p.panelOptions, p.every, !!p.intersectionObserver];
        return isDeepEqual(describe(this), describe(other));
    }

    remove(): void {
        panelModule.placements.delete(this);
        this.intersectionObserver?.disconnect();
        for (const [container, handler] of this.gates) {
            // A replaced gate's container holds another of our gates now
            if (handler?.isReplaced()) {
                continue;
            }
            handler?.remove();
            if (this.every) {
                container.removeAttribute(PANEL_INSERTED_ATTR);
            }
        }
        this.gates.clear();
    }
}

class PanelModule {
    // The queue holds arguments objects, which TypeScript cannot reconcile with the fixed-arity call they
    // are replayed into below: typing the field breaks the cast there, and vice versa
    pendingRequests = [];
    // What the scan keeps filled: the default containers, plus what journeys put up.
    placements = new Set<PanelPlacement>();
    // Each iframe calls the API as it loads, and the API throttles per IP. This caps how fast we create them, the
    // widget included, so a page that keeps re-rendering our containers can't have us create them in a loop.
    iframeThrottler = new SessionActionThrottler("panel-iframes", [
        {count: 8, duration: 10, scope: StorageScope.MEMORY},
        {count: 20, duration: 60, scope: StorageScope.MEMORY},
    ]);
    warnedIFrameBudget = false;
    cachedBannerGates = new Map(); // TODO @flow this is a temp hack for banners, so that only a single banner of a specific type is displayed. Merge with popup dedup logic

    init() {
        // The widget's iframe, just created by walletModule
        this.iframeThrottler.execute();

        // Pre-create the modal panel.
        const panel = new PanelElement({type: PANEL_TYPE.empty, gateType: GATE_TYPE.popup}, walletModule.widget.node);
        panel.initialize();

        for (const {selector, ...options} of DEFAULT_PANEL_CONTAINERS) {
            this.addPermanentPlacement(new PanelPlacement(selector, options, {every: true}));
        }
        // TODO(@sdk-deprecated): We shouldn't automatically add an interval anymore.
        setInterval(() => this.scanPlacements(), 200);
    }

    addPlacement(placement: PanelPlacement): void {
        try {
            document.querySelector(placement.selector);
        } catch (e) {
            // A bad selector would fail every scan
            merchantFunctionModule.handleException(e, placement.panelOptions.journeyAlias);
            return;
        }
        this.placements.add(placement);
        placement.scan();
    }

    // For a placement nobody will remove. What made it can run again (a blink action, say), and each copy would
    // add a scan that never ends.
    addPermanentPlacement(placement: PanelPlacement): void {
        if (Array.from(this.placements).some(existing => existing.permanent && existing.isSameAs(placement))) {
            return;
        }
        placement.permanent = true;
        this.addPlacement(placement);
    }

    scanPlacements(): void {
        // Single placements first, so a paywall going back up gets the iframe budget ahead of the loops' panels
        for (const placement of Array.from(this.placements).sort((a, b) => Number(a.every) - Number(b.every))) {
            placement.scan();
        }
    }

    resolvePendingRequests() {
        const pendingRequests = this.pendingRequests;
        this.pendingRequests = null;
        for (const args of pendingRequests) {
            this.createPanel(...(args as [any, any]));
        }
        this.scanPlacements();
    }

    // Whether the budget has room for another iframe.
    hasIFrameRoom(): boolean {
        if (this.iframeThrottler.execute(true)) {
            return true;
        }
        if (!this.warnedIFrameBudget) {
            console.warn("[BlinkSDK] Creating panels too fast, holding some back.");
            this.warnedIFrameBudget = true;
        }
        return false;
    }

    createPanel(nodesOrSelector: Element | string | null, options: PanelElementOptions): InteractiveHandlers | undefined {
        nodesOrSelector = nodesOrSelector || DEFAULT_CONTAINER_SELECTOR;

        // If the above method hasn't been called, this class can't create panels, so it has to queue them.
        // The place where the previous method gets called is relevant.
        if (this.pendingRequests != null) {
            this.pendingRequests.push(arguments);
            return;
        }

        // Transition from multiple possible nodes to a single one per createPanel call.
        if (isString(nodesOrSelector)) {
            const queryString = nodesOrSelector;
            nodesOrSelector = document.querySelector(queryString);
            if (!nodesOrSelector) {
                console.warn(`[Blink] Failed to find node (selector: ${queryString}) for panel creation.`);
                return;
            }
        }

        // STEP 1: Build the final options and prepare to instantiate the panel.

        options = evaluatePanelOptions(options);

        if (options.redirectUrl && options.skipCTA) {
            const isPopup = options.gateType === GATE_TYPE.popup;
            const condition = options.redirectCondition;
            if (!condition
                || (condition === "inline" && !isPopup)
                || (condition === "popup" && isPopup)) {
                window.location.href = options.redirectUrl;
                return;
            }
        }

        const removeAnonymousFunctionJobs = new CleanupJobs();
        // All the functions added for the panel are passed through the merchant function module. We need it so that the
        // errors don't get thrown and our code doesn't break.
        for (const maybeFunctionOption of ["onFlowCheckpoint", "onFlowFinish", "onFlowSuccess", "onMount"]) {
            if (typeof options[maybeFunctionOption] === "function") {
                const functionKey = merchantFunctionModule.addAnonymous(options[maybeFunctionOption]);
                options[maybeFunctionOption] = functionKey;
                removeAnonymousFunctionJobs.add(() => merchantFunctionModule.delete(functionKey));
            }
        }

        const gateType = options.gateType;

        if (!isMerchantActive()) {
            console.error("[BlinkSDK] Blink SDK is inactive!");
            return;
        }

        // STEP 2: Instantiate and mount the panel and the gate.
        const domNode = nodesOrSelector as HTMLElement;
        let panel, handler;

        if (gateType === GATE_TYPE.popup) {
            // Only one popup may be active at a time, so a global panel is used
            const modalObj = walletModule.showModal(options); // TODO this is called from walletModule since they share an iframe
            panel = modalObj.panel;
            handler = modalObj.handler;
        } else {
            // Create a panel, create a gate that contains that panel, mount the gate in the DOM and build the handler.
            const GateClass = gateType ? gateMap[gateType] : GateElement;

            if (GateClass == null) {
                console.error("[BlinkSDK] Unknown gate type", gateType);
                return;
            }

            const panelAlias = options.panel;

            // No panelAlias means we're in preview. Make explicit. TODO @flow2 fix caching even if the node gets destroyed in all cases
            const cachedGate = gateType === GATE_TYPE.banner && panelAlias ? this.cachedBannerGates.get(panelAlias) : null;
            // We can get here before the gate's own check has noticed the merchant's router discarded it, and
            // reusing a dead one would silently stop the banner from ever showing again. Asking now tears it
            // down instead, which is also what drops it from the cache.
            if (cachedGate?.checkStillMounted()) {
                handler = cachedGate.getInteractiveHandlers();
                panel = cachedGate.panel;
                panel.updateOptions(options);
            } else {
                // Counted when the budget has room, never held back: only placements wait, since they retry.
                this.iframeThrottler.execute();
                panel = new PanelElement(options);
                const gate = new GateClass(options, panel);
                gate.mount(domNode);
                handler = gate.getInteractiveHandlers();

                if (gateType === GATE_TYPE.banner) {
                    this.cachedBannerGates.set(panelAlias, gate);
                    // Drop the cache entry once the banner is destroyed so a torn-down gate isn't
                    // reused as a dead instance after a later navigation.
                    panel.addCleanupJob(() => {
                        if (this.cachedBannerGates.get(panelAlias) === gate) {
                            this.cachedBannerGates.delete(panelAlias);
                        }
                    });
                }
            }
        }

        // STEP 3: Handle all the listeners and the post mount logic of the panel.

        // Remove all of the panel's functions lying in the merchant function module once the panel is destroyed.
        panel.addCleanupJob(removeAnonymousFunctionJobs);

        // Map the flow functions to the corresponding listeners.
        if (options.onFlowFinish) {
            panel.flowFinish.addListener((...args: any[]) => merchantFunctionModule.call(options.onFlowFinish, ...args));
        }

        if (options.onFlowCheckpoint) {
            panel.flowCheckpoint.addListener((event: {type: string}) => merchantFunctionModule.call(options.onFlowCheckpoint, event));
        }

        const onFlowSuccess = (flowType: string) => {
            // TODO @flow2 this is a hack to keep a thank-you screen open after the flow succeeded
            if (gateType !== GATE_TYPE.popup || (flowType !== FLOW_TYPE.donation && flowType !== FLOW_TYPE.subscription)) {
                handler.remove();
            }

            if (options.onFlowSuccess) {
                merchantFunctionModule.call(options.onFlowSuccess);
            }
        };

        const flowSuccessType = panel.isFlowSuccess();
        if (flowSuccessType) {
            onFlowSuccess(flowSuccessType);
        } else {
            panel.flowSuccess.addListener(onFlowSuccess);
        }

        if (options.onMount) {
            merchantFunctionModule.call(options.onMount, handler);
        }

        return handler;
    }

    createPopup(options: PanelElementOptions) {
        this.createPanel(document.body, {
            ...options,
            gateType: GATE_TYPE.popup,
        });
    }
}

export const panelModule = new PanelModule();
